<template>
  <div class="column">
    <woot-modal-header :header-title="$t('CHAT_LIST.EXPORT.FILTER.TITLE')">
      <p>{{ $t('CHAT_LIST.EXPORT.FILTER.SUBTITLE') }}</p>
    </woot-modal-header>
    <div class="column modal-content">
      <div class="medium-12 columns filters-wrap">
        <filter-input-box
          v-for="(filter, i) in appliedFilters"
          :key="i"
          v-model="appliedFilters[i]"
          :filter-groups="filterGroups"
          :input-type="getInputType(appliedFilters[i].attribute_key)"
          :operators="getOperators(appliedFilters[i].attribute_key)"
          :dropdown-values="getDropdownValues(appliedFilters[i].attribute_key)"
          :show-query-operator="i !== appliedFilters.length - 1"
          :query-operator-options="['and']"
          :show-user-input="showUserInput(appliedFilters[i].filter_operator)"
          :grouped-filters="true"
          :v="$v.appliedFilters.$each[i]"
          @resetFilter="resetFilter(i, appliedFilters[i])"
          @removeFilter="removeFilter(i)"
        />
        <div class="filter-actions">
          <woot-button
            icon="add"
            color-scheme="success"
            variant="smooth"
            size="small"
            @click="appendNewFilter"
          >
            {{ $t('CHAT_LIST.EXPORT.FILTER.ADD_NEW_FILTER') }}
          </woot-button>
        </div>
      </div>
      <div class="medium-12 columns">
        <div class="modal-footer justify-content-end w-full">
          <woot-button class="button clear" @click.prevent="onClose">
            {{ $t('CHAT_LIST.EXPORT.FILTER.CANCEL_BUTTON_LABEL') }}
          </woot-button>
          <woot-button @click="submitExport">
            {{ $t('CHAT_LIST.EXPORT.FILTER.SUBMIT_BUTTON_LABEL') }}
          </woot-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import FilterInputBox from '../FilterInput/Index.vue';
import filterTypes, { filterAttributeGroups } from './advancedFilterItems';
import lastMessage from './advancedFilterItems/last_message';

// Subset of the conversation advanced-filter attributes relevant for MARVEL-1484:
// Status, Caixa de entrada, Time atribuído, Usuário atribuído, Criado em,
// Última atividade e Última mensagem (Agente ou Cliente).
const ALLOWED_ATTRIBUTE_KEYS = [
  'status',
  'assignee_id',
  'inbox_id',
  'team_id',
  'created_at',
  'last_activity_at',
  'last_message',
];

const DEFAULT_FILTER = {
  attribute_key: 'status',
  filter_operator: 'equal_to',
  values: '',
  query_operator: 'and',
  attribute_model: 'standard',
};

export default {
  components: {
    FilterInputBox,
  },
  mixins: [alertMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  validations: {
    appliedFilters: {
      required,
      $each: {
        values: {
          ensureBetween0to999(value, prop) {
            if (prop.filter_operator === 'days_before') {
              return parseInt(value, 10) > 0 && parseInt(value, 10) < 999;
            }
            return true;
          },
          required: requiredIf(prop => {
            return !(
              prop.filter_operator === 'is_present' ||
              prop.filter_operator === 'is_not_present'
            );
          }),
        },
      },
    },
  },
  data() {
    return {
      appliedFilters: [{ ...DEFAULT_FILTER }],
      filterTypes,
    };
  },
  computed: {
    filterGroups() {
      return [
        {
          name: this.$t('FILTER.GROUPS.STANDARD_FILTERS'),
          attributes: filterAttributeGroups[0].attributes.filter(attribute =>
            ALLOWED_ATTRIBUTE_KEYS.includes(attribute.key)
          ),
        },
      ];
    },
  },
  methods: {
    getInputType(key) {
      return this.filterTypes.find(filter => filter.attributeKey === key)
        ?.inputType;
    },
    getOperators(key) {
      return this.filterTypes.find(filter => filter.attributeKey === key)
        ?.filterOperators;
    },
    getDropdownValues(key) {
      const statusFilters = this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS');
      switch (key) {
        case 'status':
          return Object.keys(statusFilters).map(status => ({
            id: status,
            name: statusFilters[status].TEXT,
          }));
        case 'assignee_id':
          return this.$store.getters['agents/getAgents'];
        case 'inbox_id':
          return this.$store.getters['inboxes/getInboxes'];
        case 'team_id':
          return this.$store.getters['teams/getTeams'];
        case 'last_message':
          return lastMessage;
        default:
          return undefined;
      }
    },
    appendNewFilter() {
      this.appliedFilters.push({ ...DEFAULT_FILTER });
    },
    removeFilter(index) {
      if (this.appliedFilters.length <= 1) {
        this.showAlert(this.$t('FILTER.FILTER_DELETE_ERROR'));
      } else {
        this.appliedFilters.splice(index, 1);
      }
    },
    resetFilter(index, currentFilter) {
      this.appliedFilters[index].filter_operator = this.filterTypes.find(
        filter => filter.attributeKey === currentFilter.attribute_key
      ).filterOperators[0].value;
      this.appliedFilters[index].values = '';
    },
    showUserInput(operatorType) {
      if (operatorType === 'is_present' || operatorType === 'is_not_present')
        return false;
      return true;
    },
    submitExport() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      this.$emit('export', JSON.parse(JSON.stringify(this.appliedFilters)));
    },
  },
};
</script>
<style lang="scss" scoped>
.filters-wrap {
  padding: var(--space-normal);
  border-radius: var(--border-radius-large);
  border: 1px solid var(--color-border);
  background: var(--color-background-light);
  margin-bottom: var(--space-normal);
}

.filter-actions {
  margin-top: var(--space-normal);
}
</style>
